// The actions of the other person in a room, played on this screen. Each one
// waits for a quiet board and gets the beats the AI gets for the same action:
// a cast says who casts what, an aim marks its pick, and a move lifts the piece,
// carries it, and puts it down. Then the action goes into the match. So the
// other person never moves faster than this screen can show.

import { BEAT_LIFT_MS, BEAT_PICK_MS, BEAT_RECAP_MS } from '../constants';
import { squareName } from '../engine/squares';
import { slotsOf } from '../game/casters';
import { regionOfPick, removedByPick } from '../game/flow';
import type { RecordedAction } from '../game/replay';
import { carryMoveMs } from '../motion/carry';
import { describePiece, OWNER_NAMES } from '../spells/helpers';
import { spellById } from '../spells/library';
import { endCarry, liftPiece, releasePiece } from './carry';
import { beat, playBeat, playBeatAndHold, whenQuiet } from './director';
import { applyRecorded, gameStore } from './game';
import { describeIntent, moveLine } from './moveBeats';

let queue: RecordedAction[] = [];
let running = false;
/** One more for each reset. A run that sees another number drops what it was doing. */
let generation = 0;
/** Called when an action does not fit the match on this screen. The room asks for the whole match. */
let onMisfit: () => void = () => {};

export const setRemoteMisfit = (handler: () => void): void => {
  onMisfit = handler;
};

/** Actions waiting for their turn on the screen. */
export const remoteQueued = (): number => queue.length;

/** Drops the waiting actions: a new match, or the whole match from the server. */
export const resetRemote = (): void => {
  queue = [];
  generation += 1;
};

/** The beats before the action lands. False when a reset came while they played. */
const beatsBefore = async (action: RecordedAction, mine: number): Promise<boolean> => {
  const state = gameStore.get();
  const actor = OWNER_NAMES[state.turn];
  if (action.kind === 'cast') {
    const held = slotsOf(state, state.turn)[action.slot];
    const spell = held?.spellId ? spellById(held.spellId) : null;
    if (held && spell) {
      const price = held.kind === 'scroll' ? 'It uses a scroll.' : `It pays ${held.cost} energy.`;
      await playBeat(
        beat(`${actor} casts ${spell.name}`, price, { tone: spell.polarity }),
        BEAT_PICK_MS,
      );
    }
  } else if (action.kind === 'aim' && state.pendingSpell) {
    const spell = spellById(state.pendingSpell);
    const region = regionOfPick(state, action.square);
    const piece = state.board[action.square];
    const what = region
      ? `the squares around ${squareName(region.middle)}`
      : piece
        ? describePiece(piece, action.square)
        : squareName(action.square);
    const marks = region
      ? region.squares.map((square) => ({ square, kind: 'consider' as const }))
      : [
          { square: action.square, kind: 'pick' as const },
          ...removedByPick(state, action.square).map((square) => ({
            square,
            kind: 'remove' as const,
          })),
        ];
    await playBeat(
      beat(`${actor} picks ${what}`, spell.blurb, { marks, tone: spell.polarity, spotlight: true }),
      BEAT_PICK_MS,
    );
  }
  return mine === generation;
};

/** A move: the piece comes up, travels as the move lands, and comes down. Then the recap. */
const playMove = async (action: RecordedAction & { kind: 'move' }, mine: number): Promise<void> => {
  const before = gameStore.get();
  const intent = describeIntent(before, action.move);
  const mover = before.board[action.move.from];
  const plan = mover ? liftPiece(mover.id, action.move.from, action.move.to) : null;
  await playBeatAndHold(intent, BEAT_LIFT_MS);
  if (mine !== generation) {
    endCarry();
    return;
  }
  releasePiece();
  if (!applyRecorded(action)) {
    endCarry();
    onMisfit();
    return;
  }
  const after = gameStore.get();
  if (plan) await playBeatAndHold({ ...intent, marks: [] }, carryMoveMs(plan));
  endCarry();
  await playBeat(beat(`${OWNER_NAMES[before.turn]} moved`, moveLine(before, after)), BEAT_RECAP_MS);
};

const run = async (): Promise<void> => {
  if (running) return;
  running = true;
  try {
    while (queue.length > 0) {
      const mine = generation;
      await whenQuiet();
      const action = queue[0];
      if (mine !== generation || !action) continue;
      if (action.kind === 'move') {
        queue.shift();
        await playMove(action, mine);
        continue;
      }
      if (!(await beatsBefore(action, mine))) continue;
      queue.shift();
      if (!applyRecorded(action)) onMisfit();
    }
  } finally {
    running = false;
  }
};

/** An action of the other person, in the order of the record. */
export const queueRemote = (action: RecordedAction): void => {
  queue.push(action);
  void run();
};
